import { useCallback, useEffect, useRef, useState } from 'react'
import type { CalendarColorValue } from './use-calendars'

export interface CalendarMetadataChange {
  calendarId: string
  enabled?: boolean
  color?: CalendarColorValue
}

interface PendingChanges {
  [calendarId: string]: {
    enabled?: boolean
    color?: CalendarColorValue
  }
}

const DEBOUNCE_MS = 1500

export function useDebouncedCalendarSave(onSaveChanges: (changes: Array<CalendarMetadataChange>) => Promise<void>) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveInProgressRef = useRef(false)
  const pendingChangesRef = useRef<PendingChanges>({})
  const [isSaving, setIsSaving] = useState(false)
  const [pendingCalendarIds, setPendingCalendarIds] = useState<Set<string>>(new Set())
  const [savingCalendarIds, setSavingCalendarIds] = useState<Set<string>>(new Set())

  // Stable ref so the flush effects (unmount/visibility) don't depend on the
  // identity of the caller-provided save function.
  const onSaveChangesRef = useRef(onSaveChanges)
  onSaveChangesRef.current = onSaveChanges

  // Persist whatever is currently pending. Safe to call from the debounce
  // timer or directly (flush). Drains changes that arrive mid-save so nothing
  // is lost.
  const executePendingSave = useCallback(async () => {
    if (saveInProgressRef.current) return
    if (Object.keys(pendingChangesRef.current).length === 0) return

    setIsSaving(true)
    saveInProgressRef.current = true

    try {
      while (Object.keys(pendingChangesRef.current).length > 0) {
        const changes: Array<CalendarMetadataChange> = Object.entries(pendingChangesRef.current).map(
          ([calendarId, change]) => ({ calendarId, ...change }),
        )

        setSavingCalendarIds(new Set(changes.map((c) => c.calendarId)))

        // Clear before awaiting so changes queued during the save are picked up
        // by the next loop iteration rather than dropped.
        pendingChangesRef.current = {}
        setPendingCalendarIds(new Set())

        await onSaveChangesRef.current(changes)

        setSavingCalendarIds(new Set())
      }
    } catch (error) {
      pendingChangesRef.current = {}
      setPendingCalendarIds(new Set())
      setSavingCalendarIds(new Set())
      console.error('[useDebouncedCalendarSave] Failed to persist calendar changes:', error)
    } finally {
      saveInProgressRef.current = false
      setIsSaving(false)
    }
  }, [])

  // Cancel the debounce and persist immediately. Fire-and-forget — callers
  // (unmount / window hide) can't await.
  const flushNow = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    void executePendingSave()
  }, [executePendingSave])

  const queueChange = useCallback(
    async (calendarId: string, change: { enabled?: boolean; color?: CalendarColorValue }) => {
      // Merge change into pending changes for this calendar
      pendingChangesRef.current[calendarId] = {
        ...pendingChangesRef.current[calendarId],
        ...change,
      }

      // Update pending calendar IDs for UI feedback
      setPendingCalendarIds((prev) => new Set(Array.from(prev).concat(calendarId)))

      // Reset the debounce window
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null
        void executePendingSave()
      }, DEBOUNCE_MS)
    },
    [executePendingSave],
  )

  // Flush (don't drop) pending changes on unmount — e.g. switching settings
  // tabs within the debounce window unmounts the Calendars tab.
  useEffect(() => {
    return () => {
      flushNow()
    }
  }, [flushNow])

  // The settings BrowserWindow is hidden right before it's destroyed on close
  // (see WindowManager's settings close handler). Hiding fires visibilitychange
  // while the renderer is still alive, giving us a reliable, IPC-free hook to
  // persist pending edits before the window goes away.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) flushNow()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [flushNow])

  return { queueChange, isSaving, pendingCalendarIds, savingCalendarIds }
}
